import { ColorScheme } from '@design/design-tokens/themes/themes';
import { Indicator, useWindowResize } from '@helpers';
import * as d3 from 'd3';
import React, { useMemo, useRef } from 'react';

import { InvertedThresholdArea } from './components/InvertedThresholdArea/InvertedThresholdArea';
import { ThresholdIconsOverlay } from './components/ThresholdIconsOverlay/ThresholdIconsOverlay';
import styles from './TadpoleChart.module.scss';
import { renderBounds, Variant } from './utils';

export interface IndexedTsPoint {
  /** Vertical position (used as the y-axis). */
  index: number;
  /** Indicator value (used as the x-axis). */
  value: number;
  /** Timestamp of the point. */
  date: Date;
}

export interface TadpoleChartProps {
  /** Series of indexed time-series points to plot as the tadpole's tail. */
  tsData: IndexedTsPoint[];
  /** Upper threshold value on the indicator axis. */
  upperBound: number;
  /** Lower threshold value on the indicator axis. */
  lowerBound: number;
  /** Mid-value on the indicator axis (e.g. neutral / zero). */
  midValue: number;
  /** Variant per side `[lower, upper]` controlling threshold color and orientation. */
  thresholdVariants: [Variant, Variant];
  /** Indicator metadata used for labelling (e.g. axis title, formatter). */
  indicator: Indicator;
  /** Color scheme override (light / dark). Defaults to the active theme. */
  colorScheme?: ColorScheme;
  /** Show numeric values for `lowerBound`, `midValue`, and `upperBound` on the axis. Defaults to `false`. */
  isShowBoundNumbers?: boolean;
}

/**
 * Tadpole-shaped indicator chart: the head is the most recent value
 * (drawn as a marker against threshold zones) and the tail traces how
 * the indicator arrived at that value over time.
 *
 * Used in dashboards for "where are we now and how did we get here?"
 * indicator displays where threshold context (low / neutral / high)
 * matters as much as the trajectory.
 */
export const TadpoleChart = ({
  tsData,
  upperBound,
  lowerBound,
  thresholdVariants,
  midValue,
  indicator,
  colorScheme,
  isShowBoundNumbers = false,
}: TadpoleChartProps) => {
  const targetRef = useRef(null);
  const lowerBoundRef = useRef<SVGTextElement | null>(null);
  const middleBoundRef = useRef<SVGTextElement | null>(null);
  const upperBoundRef = useRef<SVGTextElement | null>(null);

  const { width, height } = useWindowResize({ targetRef });

  const yScale = useMemo(() => {
    const [minY = 0, maxY = 0] = d3.extent(tsData.map(p => p.index));
    const yAxisPadding = Math.max((maxY - minY) * 0.25, 1e-6);
    return d3
      .scaleLinear()
      .domain([
        tsData[0]?.index - yAxisPadding,
        tsData[tsData.length - 1]?.index + yAxisPadding,
      ])
      .range([height, 0]);
  }, [tsData, height]);

  const xScale = useMemo(() => {
    const [minTs = 0, maxTs = 0] = d3.extent(tsData.map(p => p.value));
    const [minX = 0, maxX = 0] = [
      Math.min(lowerBound, minTs),
      Math.max(upperBound, maxTs),
    ];
    const xValPadding = Math.max((maxX - minX) * 0.25, 1e-6);
    const extendedXDomain = [minX - xValPadding, maxX + xValPadding];
    const adjustedRange =
      thresholdVariants[1] === 'negative' ? [0, width] : [width, 0];
    return d3.scaleLinear().domain(extendedXDomain).range(adjustedRange);
  }, [tsData, width]);

  const plotLine = useMemo(
    () =>
      d3
        .line<(typeof tsData)[0]>()
        .y(p => yScale(p.index))
        .x(p => xScale(p.value))(tsData) ?? '',
    [yScale, xScale, tsData]
  );

  const maxValue = d3.max(tsData, d => d.value);

  if (maxValue === undefined) {
    return null;
  }

  const [leftValue, rightValue] =
    thresholdVariants[1] === 'negative'
      ? [lowerBound, upperBound]
      : [upperBound, lowerBound];
  const midLineOffset = xScale(midValue);
  const toUpperBound = xScale(rightValue);
  const toLowerBound = xScale(leftValue);
  const bullishAreaWidth = Math.max(
    toLowerBound,
    Math.max(...xScale.range()) - toUpperBound
  );
  const bearishAreaWidth = toLowerBound;

  return (
    <section>
      {isShowBoundNumbers && (
        <svg className={styles.svgBounds}>
          {renderBounds({
            ref: lowerBoundRef,
            value: leftValue,
            fill: 'var(--viz-bearish)',
            xPosition: toLowerBound,
          })}
          {renderBounds({
            ref: middleBoundRef,
            value: midValue,
            fill: 'var(--text-soft)',
            xPosition: midLineOffset,
          })}
          {renderBounds({
            ref: upperBoundRef,
            value: rightValue,
            fill: 'var(--viz-bullish)',
            xPosition: toUpperBound,
          })}
        </svg>
      )}
      <svg
        className={styles.svgChart}
        data-testid="inverted-threshold-chart"
        ref={targetRef}
      >
        <rect className={styles.rectBackground} />

        <line
          className={styles.zeroLine}
          data-testid="zero-line"
          y1="0"
          x1={midLineOffset}
          y2="100%"
          x2={midLineOffset}
        />

        {tsData.map((data, index) => {
          const position = yScale(data.index);

          return (
            <line
              key={index}
              x1="0"
              y1={position}
              x2="100%"
              y2={position}
              stroke="var(--border-soft)"
              strokeWidth="0.5"
            />
          );
        })}

        <path
          className={styles.pathPlot}
          data-testid="plotted-path"
          d={plotLine}
        />

        <InvertedThresholdArea
          width={bearishAreaWidth}
          edgeOffset={toLowerBound}
          variant="negative"
          colorScheme={colorScheme}
        />

        <InvertedThresholdArea
          width={bullishAreaWidth}
          offset={toUpperBound}
          variant="positive"
          colorScheme={colorScheme}
        />

        <ThresholdIconsOverlay
          xScale={xScale}
          yScale={yScale}
          ts={tsData}
          lowerBound={lowerBound}
          upperBound={upperBound}
          indicator={indicator}
        />
      </svg>
    </section>
  );
};
